import ItemParticle from "./ItemParticle";
import UIHandler from "./uiHandler";

class FluidCanvas {
  private initialized = false;
  private THREE: any;
  private Matter: any;
  private engine: any;
  private world: any;
  private noise: any;
  private canvasContainer: HTMLElement | null = null;
  private interfaceCanvasContainer: HTMLElement | null = null;
  private scrollContainer: HTMLElement | null = null;
  private categoryContainer: HTMLElement | null = null;
  private fluidContainer: HTMLElement | null = null;
  private loaderElement: HTMLElement | null = null;
  private scene: any = null;
  private camera: any = null;
  private renderer: any = null;
  private interfaceRenderer: any = null;
  private items: ItemParticle[] = [];
  private loadingComplete: boolean = false;
  private connectionLines: any[] = []; // Lines connecting filters to items
  private uiHandler: UIHandler | null = null;
  private lastFrameTime: number = 0;
  private readonly TARGET_FPS: number = 30;
  private readonly FRAME_INTERVAL: number = 1000 / 30; // ~33.33ms between frames
  private firstRenderFrame: boolean = true;
  private loseContextExtension: any = null; // WebGL debug extension for testing context loss
  private isPaused: boolean = false;

  constructor() {
    // Add keyboard shortcut to trigger context loss for debugging
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", (e) => {
        // Press Ctrl+Shift+L to force context loss
        if (e.ctrlKey && e.shiftKey && e.key === "L") {
          this.debugForceContextLoss();
        }
      });

      // Listen for pause/unpause events from lightbox
      window.addEventListener("pauseFluidCanvas", () => {
        this.pause();
      });

      window.addEventListener("unpauseFluidCanvas", () => {
        this.resume();
      });
    }
  }

  /**
   * Async initialization for heavy lifting (loading libraries, data, etc.)
   * Call this method to start the async init process without blocking.
   */
  async init(): Promise<void> {
    if (this.initialized) {
      console.warn("FluidCanvas already initialized");
      return;
    }

    console.log("FluidCanvas: Starting async initialization...");

    try {
      // Load libraries, data, etc. here
      await this.loadDependencies();
      await this.setupCanvas();
      await this.initItems();
      await this.initUI();

      this.initialized = true;

      // Remove preInit class after initialization
      if (this.fluidContainer) {
        this.fluidContainer.classList.remove("preInit");
      }

      console.log("FluidCanvas: Initialization complete");
    } catch (error) {
      console.error("FluidCanvas: Initialization failed", error);
      throw error;
    }
  }

  private async initUI(): Promise<void> {
    this.uiHandler = new UIHandler();
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log("FluidCanvas: UI Handler initialized");
        resolve();
      }, 10);
    });
  }

  /**
   * Load external libraries or heavy dependencies
   */
  private async loadDependencies(): Promise<void> {
    try {
      this.THREE = await import("three");
      const MatterModule = await import("matter-js");
      this.Matter = MatterModule.default || MatterModule;
      const { createNoise3D } = await import("simplex-noise");
      this.noise = createNoise3D();

      // Initialize Matter.js engine
      this.engine = this.Matter.Engine.create({
        gravity: { x: 0, y: 0, scale: 0.001 }, // Slight upward gravity
      });
      this.world = this.engine.world;

      console.log("Three.js, Matter.js, and simplex-noise loaded successfully");
    } catch (error) {
      console.error("Failed to load dependencies:", error);
      throw error;
    }
  }

  /**
   * Setup the canvas and WebGL context
   */
  private async setupCanvas(): Promise<void> {
    // Setup WebGL context, create scene, camera, etc.
    this.canvasContainer = document.querySelector(".canvas-container");
    this.interfaceCanvasContainer = document.querySelector(
      ".interface-canvas-container"
    );
    this.fluidContainer = document.querySelector(".fluid-container");
    this.loaderElement = document.querySelector(".canvas-loader");
    this.categoryContainer = document.querySelector(".category-container");
    if (!this.canvasContainer) {
      throw new Error("Canvas container not found");
    }
    if (!this.interfaceCanvasContainer) {
      throw new Error("Interface canvas container not found");
    }

    this.scrollContainer = document.querySelector(".scroll-content-container");
    if (!this.scrollContainer) {
      throw new Error("Scroll content container not found");
    }

    this.scene = new this.THREE.Scene();

    // OrthographicCamera with origin at top-left (like DOM/CSS coordinates)
    // Parameters: left, right, top, bottom, near, far
    this.camera = new this.THREE.OrthographicCamera(
      0, // left edge
      window.innerWidth, // right edge
      0, // top edge
      -window.innerHeight, // bottom edge (negative to flip Y axis)
      0,
      1000
    );
    this.renderer = new this.THREE.WebGLRenderer({
      antialias: true,
      transparent: true,
      powerPreference: "high-performance", // Request dedicated GPU
      failIfMajorPerformanceCaveat: false, // Allow fallback to software rendering
    });
    // Limit pixel ratio to prevent excessive GPU memory usage on Retina displays
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 0.1));
    this.renderer.setClearColor(0xffffff, 0); // Transparent background

    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.canvasContainer.appendChild(this.renderer.domElement);

    // Add context loss handlers
    this.renderer.domElement.addEventListener(
      "webglcontextlost",
      (event: Event) => {
        event.preventDefault();
        console.warn("WebGL context lost on main renderer");
        this.handleContextLoss();
      }
    );

    this.renderer.domElement.addEventListener("webglcontextrestored", () => {
      console.log("WebGL context restored on main renderer");
      this.handleContextRestore();
    });

    // Create interface renderer with same settings
    this.interfaceRenderer = new this.THREE.WebGLRenderer({
      antialias: true,
      transparent: true,
      powerPreference: "high-performance",
      failIfMajorPerformanceCaveat: false,
    });
    this.interfaceRenderer.setPixelRatio(window.devicePixelRatio, 1.0);
    this.interfaceRenderer.setClearColor(0xffffff, 0);
    this.interfaceRenderer.setSize(window.innerWidth, window.innerHeight);
    this.interfaceCanvasContainer.appendChild(
      this.interfaceRenderer.domElement
    );

    // Add context loss handlers for interface renderer
    this.interfaceRenderer.domElement.addEventListener(
      "webglcontextlost",
      (event: Event) => {
        event.preventDefault();
        console.warn("WebGL context lost on interface renderer");
        this.handleContextLoss();
      }
    );

    this.interfaceRenderer.domElement.addEventListener(
      "webglcontextrestored",
      () => {
        console.log("WebGL context restored on interface renderer");
        this.handleContextRestore();
      }
    );

    // Get WebGL debug extension for testing context loss
    const gl = this.renderer.getContext();
    this.loseContextExtension = gl.getExtension("WEBGL_lose_context");
    if (this.loseContextExtension) {
      console.log(
        "WebGL context loss debugging enabled. Press Ctrl+Shift+L to force context loss."
      );
    }

    window.addEventListener("resize", () => this.resizeHandler());
    window.addEventListener("scroll", () => this.scrollHandler());
    this.resizeHandler();
    this.scrollHandler();
    requestAnimationFrame(() => this.renderLoop());
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log("FluidCanvas: Canvas setup complete");
        resolve();
      }, 10);
    });
  }

  private resizeHandler(): void {
    if (this.camera && this.renderer) {
      this.camera.left = 0;
      this.camera.right = window.innerWidth;
      this.camera.top = 0;
      this.camera.bottom = -window.innerHeight; // Keep negative for top-left origin
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.interfaceRenderer.setSize(window.innerWidth, window.innerHeight);
    }

    // Update scale factors for all particles
    this.items.forEach((item) => {
      if (typeof item.updateScaleFactor === "function") {
        item.updateScaleFactor();
      }
    });

    // Update scroll container height
    this.updateScrollContainerHeight();
  }

  private updateScrollContainerHeight(): void {
    if (!this.scrollContainer || this.items.length === 0) return;

    // Find the lowest item position (most negative Y value)
    let lowestY = 0;
    this.items.forEach((item) => {
      const itemBottom = -item.position.y + item.height / 2;
      if (itemBottom > lowestY) {
        lowestY = itemBottom;
      }
    });

    // Set height to lowest item + 50vh
    const height = lowestY + window.innerHeight * 0.15;
    this.scrollContainer.style.height = `${height}px`;
    this.categoryContainer!.style.height = `${height}px`;
  }

  private scrollHandler(): void {
    // Move camera based on scroll position
    const scrollY = window.scrollY || window.pageYOffset;
    if (this.camera) {
      this.camera.position.y = -scrollY;
      this.camera.updateProjectionMatrix();
    }

    // Move the fluid canvas container to follow scroll
    if (this.fluidContainer) {
      this.fluidContainer.style.transform = `translateY(${-scrollY}px)`;
    }
  }

  private async initItems(): Promise<void> {
    // Test item initialization
    let elements = document.querySelectorAll(".item");

    elements.forEach((element) => {
      let item = new ItemParticle(
        element,
        this.THREE,
        this.renderer,
        this.scene,
        this.noise,
        this.Matter,
        this.world
      );
      this.items.push(item);

      // Setup hover listeners
      element.addEventListener("mouseenter", () => {
        item.setHovered(true);
      });
      element.addEventListener("mouseleave", () => {
        item.setHovered(false);
      });
    });

    // Update scroll container height after items are initialized
    this.updateScrollContainerHeight();

    return new Promise((resolve) => {
      setTimeout(() => {
        console.log("FluidCanvas: Items initialized");
        resolve();
      }, 10);
    });
  }

  /**
   * Check if FluidCanvas is ready
   */
  isReady(): boolean {
    return this.initialized;
  }

  /**
   * Pause rendering and physics simulation
   * Currently disabled to avoid issues with Complianz plugin blocking initial render
   */
  pause(): void {
    // Disabled: simulation keeps running in background
    return;
  }

  /**
   * Resume rendering and physics simulation
   */
  resume(): void {
    if (this.isPaused) {
      this.isPaused = false;
      this.lastFrameTime = performance.now(); // Reset frame timing

      // Show category-container when resumed
      if (this.categoryContainer) {
        this.categoryContainer.style.display = "";
      }

      console.log("FluidCanvas: Resumed");
    }
  }

  /**
   * Run the simulation for a specified number of ticks without rendering
   * Useful for initializing particle positions when starting on a single page
   */
  runSimulationTicks(ticks: number): void {
    if (!this.initialized || !this.engine) {
      console.warn("Cannot run simulation ticks: Canvas not initialized");
      return;
    }

    console.log(`FluidCanvas: Running ${ticks} simulation ticks...`);

    for (let i = 0; i < ticks; i++) {
      // Update Matter.js physics
      this.Matter.Engine.update(this.engine, this.FRAME_INTERVAL);

      // Update all particles
      const placedFilters = this.uiHandler?.getPlacedFilterPositions() || [];
      this.items.forEach((item) => item.update(this.items, placedFilters));
    }

    console.log(`FluidCanvas: Completed ${ticks} simulation ticks`);
  }

  /**
   * Force a single render frame, even if paused.
   * Useful for ensuring textures are drawn before pausing.
   */
  forceRender(): void {
    if (!this.initialized || !this.renderer) {
      console.warn("Cannot force render: Canvas not initialized");
      return;
    }

    console.log("FluidCanvas: Forcing render...");

    // Get current placed filter positions
    const placedFilters = this.uiHandler?.getPlacedFilterPositions() || [];

    // Update all particles
    this.items.forEach((item) => item.update(this.items, placedFilters));

    // Apply opacity based on filter matching
    const hasFilters = placedFilters.length > 0;
    this.items.forEach((item) => {
      const targetOpacity =
        hasFilters && !item.matchesAnyFilter(placedFilters) ? 0.33 : 1.0;
      item.updateFilterOpacity(targetOpacity);
    });

    // Update scroll container height
    this.updateScrollContainerHeight();

    // Render main scene
    this.renderer.render(this.scene, this.camera);

    // Render interface scene
    this.items.forEach((item) => {
      if (item.instancedMesh) {
        const targetOpacity = item.isHovered() ? 1 : 0;
        item.updateHoverOpacity(targetOpacity);
      }
    });
    this.interfaceRenderer.render(this.scene, this.camera);

    console.log("FluidCanvas: Force render complete");
  }

  renderLoop(): void {
    requestAnimationFrame(() => this.renderLoop());

    // Check if we should hide the loader
    if (!this.loadingComplete) {
      this.checkLoadingComplete();
    }

    // Skip rendering if not initialized (e.g., during context loss) or paused
    if (!this.initialized || this.isPaused) {
      return;
    }

    // Limit to 30 FPS for energy efficiency
    const currentTime = performance.now();
    const elapsed = currentTime - this.lastFrameTime;

    if (elapsed < this.FRAME_INTERVAL) {
      return; // Skip this frame
    }

    // Update last frame time, accounting for any extra time beyond the interval
    this.lastFrameTime = currentTime - (elapsed % this.FRAME_INTERVAL);

    // Update Matter.js physics engine at 30 FPS
    if (this.engine) {
      this.Matter.Engine.update(this.engine, this.FRAME_INTERVAL);
    }

    // Get current placed filter positions
    const placedFilters = this.uiHandler?.getPlacedFilterPositions() || [];

    this.items.forEach((item) => item.update(this.items, placedFilters));

    // Apply opacity based on filter matching with smooth fade
    const hasFilters = placedFilters.length > 0;
    this.items.forEach((item) => {
      const targetOpacity =
        hasFilters && !item.matchesAnyFilter(placedFilters) ? 0.33 : 1.0;
      item.updateFilterOpacity(targetOpacity);
    });

    // Periodically update scroll container height as items move
    if (Math.random() < 0.02 || this.firstRenderFrame) {
      // Update ~1% of frames to avoid performance hit
      this.updateScrollContainerHeight();
    }
    this.firstRenderFrame = false;

    // Update connection lines for interface scene
    this.updateConnectionLines(placedFilters);

    // Render main scene
    this.renderer.render(this.scene, this.camera);

    // Render interface scene with opacity control
    this.items.forEach((item) => {
      if (item.instancedMesh) {
        const targetOpacity = item.isHovered() ? 1 : 0;
        item.updateHoverOpacity(targetOpacity);
      }
    });
    this.interfaceRenderer.render(this.scene, this.camera);
  }

  private updateConnectionLines(placedFilters: any[]): void {
    // Calculate required connections
    const connections: Array<{
      filterX: number;
      filterY: number;
      itemX: number;
      itemY: number;
    }> = [];

    placedFilters.forEach((filter) => {
      this.items.forEach((item) => {
        if (item.matchesAnyFilter([filter])) {
          connections.push({
            filterX: filter.x,
            filterY: -filter.y,
            itemX: item.position.x,
            itemY: item.position.y,
          });
        }
      });
    });

    // Hide excess lines if we have more than needed
    for (let i = connections.length; i < this.connectionLines.length; i++) {
      this.connectionLines[i].visible = false;
    }

    // Update or create lines as needed
    connections.forEach((connection, index) => {
      const { filterX, filterY, itemX, itemY } = connection;

      const dx = itemX - filterX;
      const dy = itemY - filterY;
      const length = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);

      // Reuse existing line if available
      if (index < this.connectionLines.length) {
        const line = this.connectionLines[index];
        line.visible = true;

        // Update geometry to new length
        const geometry = line.geometry;
        const positions = geometry.attributes.position.array;
        const lineWidth = 1;

        // Update vertices for new length
        // PlaneGeometry has 6 vertices (2 triangles)
        const halfLength = length / 2;
        const halfWidth = lineWidth / 2;

        // Bottom-left
        positions[0] = -halfLength;
        positions[1] = -halfWidth;
        // Bottom-right
        positions[3] = halfLength;
        positions[4] = -halfWidth;
        // Top-left
        positions[6] = -halfLength;
        positions[7] = halfWidth;
        // Top-right
        positions[9] = halfLength;
        positions[10] = halfWidth;

        geometry.attributes.position.needsUpdate = true;

        // Update gradient colors based on new length
        const colors = geometry.attributes.color.array;
        for (let i = 0; i < geometry.attributes.position.count; i++) {
          const x = geometry.attributes.position.getX(i);
          let t = (x + halfLength) / length; // 0 to 1
          t = 1 - t;
          t *= 2.0;
          colors[i * 4 + 3] = 0.5 * t; // Update alpha channel
        }
        geometry.attributes.color.needsUpdate = true;

        // Update position and rotation
        line.position.set((filterX + itemX) / 2, (filterY + itemY) / 2, 0);
        line.rotation.z = angle;
      } else {
        // Create new line if we need more
        const lineWidth = 1;
        const geometry = new this.THREE.PlaneGeometry(length, lineWidth, 1, 1);

        // Apply gradient colors to vertices
        const colors = new Float32Array(geometry.attributes.position.count * 4);
        for (let i = 0; i < geometry.attributes.position.count; i++) {
          const x = geometry.attributes.position.getX(i);
          let t = (x + length / 2) / length; // 0 to 1
          t = 1 - t;
          t *= 2.0;
          colors[i * 4] = 0; // R
          colors[i * 4 + 1] = 0; // G
          colors[i * 4 + 2] = 0; // B
          colors[i * 4 + 3] = 0.5 * t; // A
        }
        geometry.setAttribute(
          "color",
          new this.THREE.BufferAttribute(colors, 4)
        );

        const material = new this.THREE.MeshBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity: 1.0,
          side: this.THREE.DoubleSide,
        });

        const line = new this.THREE.Mesh(geometry, material);
        line.position.set((filterX + itemX) / 2, (filterY + itemY) / 2, 0);
        line.rotation.z = angle;

        this.scene.add(line);
        this.connectionLines.push(line);
      }
    });
  }

  private handleContextLoss(): void {
    console.warn("WebGL context lost - stopping render loop");
    // Stop the render loop to prevent errors
    this.initialized = false;
  }

  private handleContextRestore(): void {
    console.log("WebGL context restored - attempting recovery");

    // Instead of full reinitialization, just recreate the renderers
    // and restart the render loop since the scene and items are still valid
    try {
      // Remove old canvas elements
      if (
        this.renderer &&
        this.renderer.domElement &&
        this.renderer.domElement.parentNode
      ) {
        this.renderer.domElement.parentNode.removeChild(
          this.renderer.domElement
        );
      }
      if (
        this.interfaceRenderer &&
        this.interfaceRenderer.domElement &&
        this.interfaceRenderer.domElement.parentNode
      ) {
        this.interfaceRenderer.domElement.parentNode.removeChild(
          this.interfaceRenderer.domElement
        );
      }

      // Recreate main renderer with reduced quality after context loss
      this.renderer = new this.THREE.WebGLRenderer({
        antialias: true, // Disable antialiasing to reduce GPU load
        transparent: true,
        failIfMajorPerformanceCaveat: false,
      });
      // Reduce pixel ratio after context loss to prevent recurrence
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 0.1));
      this.renderer.setClearColor(0xffffff, 0);
      this.renderer.setSize(window.innerWidth, window.innerHeight);

      if (this.canvasContainer) {
        this.canvasContainer.appendChild(this.renderer.domElement);
      }

      // Re-add context loss handlers for main renderer
      this.renderer.domElement.addEventListener(
        "webglcontextlost",
        (event: Event) => {
          event.preventDefault();
          console.warn("WebGL context lost on main renderer");
          this.handleContextLoss();
        }
      );

      this.renderer.domElement.addEventListener("webglcontextrestored", () => {
        console.log("WebGL context restored on main renderer");
        this.handleContextRestore();
      });

      // Recreate interface renderer with reduced quality
      this.interfaceRenderer = new this.THREE.WebGLRenderer({
        antialias: false,
        transparent: true,
        powerPreference: "high-performance",
        failIfMajorPerformanceCaveat: false,
      });
      this.interfaceRenderer.setPixelRatio(window.devicePixelRatio, 1.0);
      this.interfaceRenderer.setClearColor(0xffffff, 0);
      this.interfaceRenderer.setSize(window.innerWidth, window.innerHeight);

      if (this.interfaceCanvasContainer) {
        this.interfaceCanvasContainer.appendChild(
          this.interfaceRenderer.domElement
        );
      }

      // Re-add context loss handlers for interface renderer
      this.interfaceRenderer.domElement.addEventListener(
        "webglcontextlost",
        (event: Event) => {
          event.preventDefault();
          console.warn("WebGL context lost on interface renderer");
          this.handleContextLoss();
        }
      );

      this.interfaceRenderer.domElement.addEventListener(
        "webglcontextrestored",
        () => {
          console.log("WebGL context restored on interface renderer");
          this.handleContextRestore();
        }
      );

      // Get WebGL debug extension again
      const gl = this.renderer.getContext();
      this.loseContextExtension = gl.getExtension("WEBGL_lose_context");
      console.log(
        "WebGL debug extension re-acquired:",
        !!this.loseContextExtension
      );

      // Recreate all mesh materials for existing items
      console.log(`Recreating meshes for ${this.items.length} items...`);
      let successCount = 0;
      this.items.forEach((item, index) => {
        try {
          if (item.recreateMeshes) {
            item.recreateMeshes(this.scene);
            successCount++;
          }
        } catch (error) {
          console.error(`Failed to recreate mesh for item ${index}:`, error);
        }
      });
      console.log(
        `Successfully recreated ${successCount}/${this.items.length} meshes`
      );

      // Clear connection lines - DON'T dispose, they're from the lost context
      console.log(
        `Clearing ${this.connectionLines.length} connection lines...`
      );
      this.connectionLines.forEach((line) => {
        if (line.parent) {
          line.parent.remove(line);
        }
        // Don't dispose geometry/material from lost context
      });
      this.connectionLines = [];

      // Restart the render loop
      this.initialized = true;
      console.log(
        "✅ WebGL context recovery complete - rendering should resume"
      );
    } catch (error) {
      console.error("❌ Failed to restore WebGL context:", error);
    }
  }

  /**
   * Check if first 5 items with images have loaded, then hide loader
   */
  private checkLoadingComplete(): void {
    // Count items with images that have loaded
    const itemsWithImages = this.items.filter((item) => item.imageUrl !== "");
    const loadedCount = itemsWithImages.filter(
      (item) => item.textureLoaded
    ).length;

    // Wait for at least 5 items to load (or all if less than 5)
    const targetCount = Math.min(5, itemsWithImages.length);

    console.log(
      `Loading progress: ${loadedCount}/${targetCount} (total with images: ${itemsWithImages.length})`
    );

    if (loadedCount >= targetCount && targetCount > 0) {
      this.loadingComplete = true;

      // Hide loader with fade out
      if (this.loaderElement) {
        setTimeout(() => {
          this.loaderElement.classList.add("loaded");
          setTimeout(() => {
            if (this.loaderElement) {
              this.loaderElement.style.display = "none";
            }
          }, 750); // Match CSS transition duration
        }, 300); // Small delay before starting fade out
      }

      console.log(
        `FluidCanvas: Loading complete - ${loadedCount}/${itemsWithImages.length} images loaded`
      );

      // Dispatch custom event to signal loading is complete
      window.dispatchEvent(new CustomEvent("fluidCanvasImagesLoaded"));
    }
  }

  /**
   * Force WebGL context loss for debugging purposes.
   * Trigger with Ctrl+Shift+L keyboard shortcut.
   */
  private debugForceContextLoss(): void {
    if (this.loseContextExtension) {
      console.log("🔴 Forcing WebGL context loss for debugging...");
      this.loseContextExtension.loseContext();

      // Automatically restore after 3 seconds to test recovery
      setTimeout(() => {
        console.log("🟢 Manually restoring WebGL context...");
        if (this.loseContextExtension) {
          this.loseContextExtension.restoreContext();
        }
        // Manually trigger restore handler in case event doesn't fire
        setTimeout(() => {
          if (!this.initialized) {
            console.log(
              "⚠️ Context not restored via event, triggering manually"
            );
            this.handleContextRestore();
          }
        }, 100);
      }, 3000);
    } else {
      console.warn("WEBGL_lose_context extension not available");
    }
  }
}

export default FluidCanvas;
